import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { Shield, Lock, FileText, Download, Eye, Loader, AlertCircle, LogOut, Edit2, X, Save, ExternalLink } from 'lucide-react'
import type { DocumentMetadata } from '../lib/storage'
import { useTenant } from '../contexts/TenantContext'

const EvidenceLibrary = () => {
  console.log('📄 EvidenceLibrary component rendering')
  const navigate = useNavigate()
  const { currentTenant } = useTenant()
  console.log('📄 Current tenant from context:', currentTenant?.id)
  const [documents, setDocuments] = useState<DocumentMetadata[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [hasAccess, setHasAccess] = useState(false)
  const [userSession, setUserSession] = useState<{id: string, email: string, name: string, company: string, role: string} | null>(null)
  console.log('📄 Component state:', { hasAccess, loading, error, documentsCount: documents.length })
  const [downloading, setDownloading] = useState<string | null>(null)
  const [authLoading, setAuthLoading] = useState(true)
  const [products, setProducts] = useState<any[]>([])
  const [selectedProduct, setSelectedProduct] = useState<string>('all')
  const [selectedDocs, setSelectedDocs] = useState<Set<string>>(new Set())
  const [bulkDownloading, setBulkDownloading] = useState(false)
  const [showBulkEditModal, setShowBulkEditModal] = useState(false)
  const [bulkEditUpdating, setBulkEditUpdating] = useState(false)
  const [bulkEditForm, setBulkEditForm] = useState<{
    visibility?: 'public' | 'gated' | 'admin'
    gated?: boolean
    ndaRequired?: boolean
    watermarked?: boolean
  }>({})

  useEffect(() => {
    initializeUserSession()
    fetchProducts()
  }, [])

  // Fetch documents after tenant is loaded and user has access
  useEffect(() => {
    console.log('📄 Document fetch useEffect triggered:', { hasAccess, tenantId: currentTenant?.id })
    if (hasAccess && currentTenant?.id) {
      console.log('📄 Conditions met, calling fetchDocuments()')
      fetchDocuments()
    } else {
      console.log('📄 Conditions not met:', { hasAccess, hasTenantId: !!currentTenant?.id })
    }
  }, [hasAccess, currentTenant?.id])

  const fetchProducts = async () => {
    try {
      const token = localStorage.getItem('auth-token')
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/products', {
        headers,
        credentials: 'include'
      })
      if (response.ok) {
        const data = await response.json()
        const products = data.success ? data.result.products : data
        setProducts(products || [])
      } else {
        console.error('Failed to fetch products:', {
          status: response.status,
          statusText: response.statusText
        })
      }
    } catch (error) {
      console.error('Error fetching products:', error, {
        message: error instanceof Error ? error.message : String(error)
      })
    }
  }

  const initializeUserSession = async () => {
    try {
      setAuthLoading(true)

      // Get token from localStorage (for VFC login) or rely on cookie (for OAuth)
      const token = localStorage.getItem('auth-token')
      console.log('Token in localStorage:', token ? 'Found' : 'Not found - checking cookie')

      // Check for valid JWT session - will check cookie if no Authorization header
      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      const response = await fetch('/api/auth/validate', {
        method: 'GET',
        credentials: 'include',
        headers
      })

      if (response.ok) {
        const data = await response.json()
        console.log('✅ Auth validation successful:', data)
        console.log('🔍 About to set user session and hasAccess=true')
        setUserSession({
          id: data.user.id,
          email: data.user.email,
          name: data.user.name,
          company: data.user.company,
          role: data.user.role
        })
        setHasAccess(true)
        console.log('✅ hasAccess set to true')
        console.log('🔍 About to call fetchDocuments directly...')
        await fetchDocuments()
        console.log('✅ fetchDocuments call completed')
      } else {
        // Not authenticated, redirect to login
        const errorText = await response.text()
        console.error('❌ Auth validation failed:', response.status, errorText)
        navigate('/login')
        return
      }
    } catch (error) {
      console.error('Auth validation error:', error)
      // Only redirect on authentication errors, not network errors
      if (error instanceof TypeError && error.message.includes('fetch')) {
        // Network error - show error message instead of redirecting
        setError('Network error. Please check your connection and try again.')
      } else {
        // Other errors - redirect to login
        navigate('/login')
      }
    } finally {
      setAuthLoading(false)
    }
  }

  const handleLogout = async () => {
    // SECURITY: Clear ALL cached data including sensitive information
    localStorage.clear()
    sessionStorage.clear()

    // Clear all cookies
    document.cookie.split(";").forEach((cookie) => {
      const name = cookie.split("=")[0].trim()
      document.cookie = `${name}=; expires=Thu, 01 Jan 1970 00:00:00 UTC; path=/;`
    })

    // Redirect to login
    navigate('/login')
  }

  const fetchDocuments = async () => {
    try {
      // Get tenant ID and version for headers
      const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id
      const version = sessionStorage.getItem('content-version') || 'live'

      // Get auth token for header
      const token = localStorage.getItem('auth-token')

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      // Add auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Add tenant headers if available (for branch deploys)
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId
        headers['X-Content-Version'] = version
      }

      console.log('📄 Fetching documents with headers:', { tenantId, version, hasToken: !!token })

      const response = await fetch('/api/documents', {
        credentials: 'include',
        headers
      })

      console.log('📄 Documents response:', { status: response.status, ok: response.ok })

      if (!response.ok) {
        const errorText = await response.text()
        console.error('📄 Documents fetch failed:', { status: response.status, error: errorText })
        throw new Error(`Failed to fetch documents: ${response.status} - ${errorText}`)
      }
      const docs = await response.json()
      console.log('📄 Received documents:', { total: docs.length })
      // Filter out hidden, archived, and deleted documents from public view
      const visibleDocs = docs.filter((doc: any) => doc && !doc.hidden && !doc.archived && !doc.deleted)
      console.log('📄 Visible documents after filtering:', { visible: visibleDocs.length })
      setDocuments(visibleDocs)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load documents')
    } finally {
      setLoading(false)
    }
  }

  const formatFileSize = (bytes: number): string => {
    const units = ['B', 'KB', 'MB', 'GB']
    let size = bytes
    let unitIndex = 0

    while (size >= 1024 && unitIndex < units.length - 1) {
      size /= 1024
      unitIndex++
    }

    return `${size.toFixed(1)} ${units[unitIndex]}`
  }

  const getCategoryIcon = (category: string) => {
    switch (category) {
      case 'certifications':
        return Shield
      case 'policies':
        return FileText
      case 'architecture':
      case 'reports':
        return Lock
      default:
        return FileText
    }
  }

  const groupDocumentsByCategory = (docs: DocumentMetadata[]) => {
    const grouped: Record<string, DocumentMetadata[]> = {}

    docs.forEach(doc => {
      if (!grouped[doc.category]) {
        grouped[doc.category] = []
      }
      grouped[doc.category].push(doc)
    })

    return Object.entries(grouped).map(([category, documents]) => ({
      name: category.charAt(0).toUpperCase() + category.slice(1),
      icon: getCategoryIcon(category),
      documents
    }))
  }

  const handleDownload = async (doc: DocumentMetadata) => {
    if (!userSession || !doc.id) {
      alert('Unable to download: User session or document ID missing')
      return
    }

    setDownloading(doc.id)

    try {
      // Build download URL
      const downloadUrl = new URL('/api/download-document', window.location.origin)
      downloadUrl.searchParams.set('id', doc.id)

      // Get tenant ID and version for headers
      const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id
      const version = sessionStorage.getItem('content-version') || 'live'

      // Get auth token for header
      const token = localStorage.getItem('auth-token')

      const headers: HeadersInit = {}

      // Add auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Add tenant headers if available (for branch deploys)
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId
        headers['X-Content-Version'] = version
      }

      // Fetch the document
      const response = await fetch(downloadUrl.toString(), {
        credentials: 'include',
        headers
      })

      if (!response.ok) {
        // Check if this is an NDA requirement error
        if (response.status === 403) {
          try {
            const errorData = await response.json()
            if (errorData.error === 'NDA acceptance required') {
              // Need to show NDA clickwrap
              // Redirect to document page which has NDA handling
              window.location.href = `/document/${doc.id}`
              return
            }
          } catch {
            // Not JSON, continue with generic error
          }
        }
        throw new Error(`Download failed: ${response.statusText}`)
      }

      // Get the blob and create download link
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = doc.title
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Create and trigger download
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()

      // Cleanup
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      // Show watermark notice if applicable
      if (doc.watermarked) {
        alert(`✓ Document downloaded with watermark\n\nDownloaded: ${doc.title}\nUser: ${userSession.email}\nTime: ${new Date().toLocaleString()}\n\nThis download has been logged for security purposes.`)
      }

    } catch (error: any) {
      console.error('Download error:', error, {
        documentId: doc.id,
        documentTitle: doc.title,
        errorMessage: error?.message,
        errorStack: error?.stack
      })
      alert(`Failed to download ${doc.title}: ${error.message}`)
    } finally {
      setDownloading(null)
    }
  }

  const toggleDocSelection = (docId: string) => {
    setSelectedDocs(prev => {
      const newSelected = new Set(prev)
      if (newSelected.has(docId)) {
        newSelected.delete(docId)
      } else {
        newSelected.add(docId)
      }
      return newSelected
    })
  }

  const selectAllDocs = () => {
    const allDocIds = new Set(filteredDocuments.map(doc => doc.id).filter(id => id))
    setSelectedDocs(allDocIds)
  }

  const deselectAllDocs = () => {
    setSelectedDocs(new Set())
  }

  const handleBulkDownload = async () => {
    if (!userSession || selectedDocs.size === 0) {
      alert('Please select documents to download')
      return
    }

    setBulkDownloading(true)

    try {
      // Get tenant ID and version for headers
      const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id
      const version = sessionStorage.getItem('content-version') || 'live'

      // Get auth token for header
      const token = localStorage.getItem('auth-token')

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      // Add auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Add tenant headers if available (for branch deploys)
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId
        headers['X-Content-Version'] = version
      }

      const response = await fetch('/api/evidence/bulk-download', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          documentIds: Array.from(selectedDocs),
          userId: userSession.id,
          userEmail: userSession.email
        })
      })

      if (!response.ok) {
        throw new Error(`Bulk download failed: ${response.statusText}`)
      }

      // Get the ZIP file
      const blob = await response.blob()
      const url = window.URL.createObjectURL(blob)

      // Extract filename from Content-Disposition header or use default
      const contentDisposition = response.headers.get('Content-Disposition')
      let filename = `evidence_documents_${new Date().toISOString().slice(0, 10)}.zip`
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename="([^"]+)"/)
        if (filenameMatch) {
          filename = filenameMatch[1]
        }
      }

      // Create and trigger download
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()

      // Cleanup
      window.URL.revokeObjectURL(url)
      document.body.removeChild(a)

      // Show success message
      const docsCount = response.headers.get('X-Documents-Count') || selectedDocs.size
      alert(`✓ Successfully downloaded ${docsCount} document(s) as ZIP\n\nUser: ${userSession.email}\nTime: ${new Date().toLocaleString()}\n\nThis download has been logged for security purposes.`)

      // Clear selection
      setSelectedDocs(new Set())

    } catch (error) {
      console.error('Bulk download error:', error)
      alert(`Failed to download documents: ${error.message}`)
    } finally {
      setBulkDownloading(false)
    }
  }

  const handleBulkEditSubmit = async () => {
    if (selectedDocs.size === 0) {
      alert('Please select documents to edit')
      return
    }

    // Validate that at least one field is selected
    const hasUpdates = Object.values(bulkEditForm).some(val => val !== undefined)
    if (!hasUpdates) {
      alert('Please select at least one attribute to update')
      return
    }

    setBulkEditUpdating(true)

    try {
      // Get tenant ID and version for headers
      const tenantId = sessionStorage.getItem('tenant-id') || currentTenant?.id
      const version = sessionStorage.getItem('content-version') || 'preview'

      // Get auth token for header
      const token = localStorage.getItem('auth-token')

      const headers: HeadersInit = {
        'Content-Type': 'application/json'
      }

      // Add auth token if available
      if (token) {
        headers['Authorization'] = `Bearer ${token}`
      }

      // Add tenant headers if available (for branch deploys)
      if (tenantId) {
        headers['X-Tenant-ID'] = tenantId
        headers['X-Content-Version'] = version
      }

      const response = await fetch('/api/documents/bulk-update', {
        method: 'POST',
        credentials: 'include',
        headers,
        body: JSON.stringify({
          documentIds: Array.from(selectedDocs),
          updates: bulkEditForm
        })
      })

      if (!response.ok) {
        throw new Error(`Bulk update failed: ${response.statusText}`)
      }

      const result = await response.json()

      // Show success message
      alert(`✓ Successfully updated ${result.updated} document(s)\n${result.failed > 0 ? `\n⚠️ Failed to update ${result.failed} document(s)` : ''}`)

      // Close modal and clear form
      setShowBulkEditModal(false)
      setBulkEditForm({})
      setSelectedDocs(new Set())

      // Refresh documents
      await fetchDocuments()

    } catch (error) {
      console.error('Bulk update error:', error)
      alert(`Failed to update documents: ${error.message}`)
    } finally {
      setBulkEditUpdating(false)
    }
  }

  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Verifying access...</p>
        </div>
      </div>
    )
  }

  if (!hasAccess) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Access Required</h1>
          <p className="text-gray-600 mb-4">You need to login to access the evidence library.</p>
          <div className="space-x-4">
            <button onClick={() => navigate('/login')} className="text-primary-600 hover:text-primary-700 font-medium">Login</button>
            <span className="text-gray-400">|</span>
            <button onClick={() => navigate('/')} className="text-primary-600 hover:text-primary-700">← Back to Trust Center</button>
          </div>
        </div>
      </div>
    )
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <Loader className="h-8 w-8 animate-spin text-primary-600 mx-auto mb-4" />
          <p className="text-gray-600">Loading evidence library...</p>
        </div>
      </div>
    )
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-50 flex items-center justify-center">
        <div className="text-center">
          <AlertCircle className="h-16 w-16 text-red-500 mx-auto mb-4" />
          <h1 className="text-2xl font-bold text-gray-900 mb-2">Error Loading Documents</h1>
          <p className="text-gray-600 mb-4">{error}</p>
          <button
            onClick={() => window.location.reload()}
            className="btn-primary"
          >
            Try Again
          </button>
        </div>
      </div>
    )
  }

  // Filter documents by selected product
  const filteredDocuments = documents.filter((doc: DocumentMetadata) => {
    if (selectedProduct === 'all') return true
    if (!doc.productIds || doc.productIds.length === 0) return true
    return doc.productIds.includes(selectedProduct)
  })

  const evidenceCategories = groupDocumentsByCategory(filteredDocuments)

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b border-gray-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Shield className="h-8 w-8 text-primary-600" />
              <h1 className="ml-3 text-2xl font-bold text-gray-900">Evidence Library</h1>
            </div>
            <div className="flex items-center space-x-4">
              {userSession && (
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-600">
                    <span className="font-medium">{userSession.name}</span>
                    <span className="mx-1">•</span>
                    <span>{userSession.company}</span>
                    <span className="mx-1">•</span>
                    <span className="text-green-600">Access Active</span>
                  </div>
                  <button
                    onClick={handleLogout}
                    className="text-gray-600 hover:text-gray-800 flex items-center text-sm"
                  >
                    <LogOut className="w-4 h-4 mr-1" />
                    Logout
                  </button>
                </div>
              )}
              <a href="/" className="text-primary-600 hover:text-primary-700">← Back to Trust Center</a>
            </div>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Access Info Banner */}
        <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mb-8">
          <div className="flex">
            <Eye className="h-5 w-5 text-blue-600 mt-0.5" />
            <div className="ml-3">
              <h3 className="text-sm font-medium text-blue-800">Authorized Access</h3>
              <p className="text-sm text-blue-700 mt-1">
                You have viewer access to gated security documentation. All downloads are logged and may be watermarked.
              </p>
            </div>
          </div>
        </div>

        {/* Product Filter */}
        {products.length > 0 && (
          <div className="mb-8 flex justify-center">
            <div className="inline-flex items-center gap-3 bg-white px-6 py-3 rounded-lg shadow-sm border border-gray-200">
              <label className="text-sm font-medium text-gray-700">Filter by Product:</label>
              <select
                value={selectedProduct}
                onChange={(e) => setSelectedProduct(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-primary-500 focus:border-primary-500 text-sm"
              >
                <option value="all">All Products</option>
                {products.map((product: any) => (
                  <option key={product.id} value={product.id}>
                    {product.name}
                  </option>
                ))}
              </select>
            </div>
          </div>
        )}

        {/* Bulk Actions Bar */}
        {filteredDocuments.length > 0 && (
          <div className="mb-6 flex items-center justify-between bg-white px-6 py-4 rounded-lg shadow-sm border border-gray-200">
            <div className="flex items-center space-x-4">
              <button
                onClick={selectAllDocs}
                className="text-sm text-primary-600 hover:text-primary-700 font-medium"
              >
                Select All ({filteredDocuments.length})
              </button>
              {selectedDocs.size > 0 && (
                <>
                  <span className="text-gray-300">|</span>
                  <button
                    onClick={deselectAllDocs}
                    className="text-sm text-gray-600 hover:text-gray-700 font-medium"
                  >
                    Deselect All
                  </button>
                  <span className="text-gray-300">|</span>
                  <span className="text-sm text-gray-600">
                    {selectedDocs.size} document{selectedDocs.size !== 1 ? 's' : ''} selected
                  </span>
                </>
              )}
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setShowBulkEditModal(true)}
                disabled={selectedDocs.size === 0}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <Edit2 className="h-4 w-4 mr-2" />
                Bulk Edit {selectedDocs.size > 0 ? `(${selectedDocs.size})` : ''}
              </button>
              <button
                onClick={handleBulkDownload}
                disabled={selectedDocs.size === 0 || bulkDownloading}
                className="inline-flex items-center px-4 py-2 border border-gray-300 text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkDownloading ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Downloading ZIP...
                  </>
                ) : (
                  <>
                    <Download className="h-4 w-4 mr-2" />
                    Download Selected {selectedDocs.size > 0 ? `(${selectedDocs.size})` : ''}
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Empty State */}
        {filteredDocuments.length === 0 ? (
          <div className="text-center py-12">
            <FileText className="h-16 w-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No Documents Available</h3>
            <p className="text-gray-600 mb-4">
              No documents have been uploaded to the evidence library yet.
            </p>
            <p className="text-sm text-gray-500">
              Documents will appear here once they are uploaded by administrators.
            </p>
          </div>
        ) : (
          /* Evidence Categories */
          evidenceCategories.map((category, index) => {
          const IconComponent = category.icon
          return (
            <div key={index} className="mb-8">
              <div className="flex items-center mb-4">
                <IconComponent className="h-6 w-6 text-gray-600 mr-2" />
                <h2 className="text-xl font-semibold text-gray-900">{category.name}</h2>
              </div>

              <div className="bg-white rounded-lg shadow-sm border border-gray-200">
                <div className="divide-y divide-gray-200">
                  {category.documents.map((doc, docIndex) => (
                    <div key={docIndex} className="p-4 hover:bg-gray-50 transition-colors">
                      <div className="flex items-center justify-between">
                        <div className="flex items-center flex-1">
                          <input
                            type="checkbox"
                            checked={selectedDocs.has(doc.id)}
                            onChange={() => toggleDocSelection(doc.id)}
                            className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded mr-4"
                          />
                          <div className="flex items-center flex-1">
                            <FileText className="h-5 w-5 text-gray-400 mr-3" />
                            <div>
                              <h3 className="text-sm font-medium text-gray-900">{doc.title}</h3>
                              <p className="text-sm text-gray-500">{formatFileSize(doc.size)}</p>
                            </div>
                          </div>
                        </div>

                        <div className="flex items-center space-x-4">
                          <div className="flex space-x-2">
                            {doc.gated && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                <Lock className="h-3 w-3 mr-1" />
                                Gated
                              </span>
                            )}
                            {doc.watermarked && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                                Watermarked
                              </span>
                            )}
                            {doc.redacted && (
                              <span className="inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium bg-orange-100 text-orange-800">
                                Redacted - {doc.allowedPages || doc.maxPages || '5'}
                              </span>
                            )}
                          </div>

                          <div className="flex gap-2">
                            {doc.mimeType === 'application/pdf' && (
                              <button
                                onClick={() => navigate(`/documents/${doc.id}`)}
                                className={`inline-flex items-center px-3 py-1.5 border text-sm font-medium rounded ${
                                  doc.redacted
                                    ? 'border-orange-300 text-orange-700 bg-orange-50 hover:bg-orange-100'
                                    : 'border-blue-300 text-blue-700 bg-blue-50 hover:bg-blue-100'
                                }`}
                              >
                                <Eye className="h-4 w-4 mr-1" />
                                {doc.redacted ? 'View Preview' : 'View'}
                              </button>
                            )}
                            <button
                              onClick={() => handleDownload(doc)}
                              disabled={downloading === doc.id}
                              className="inline-flex items-center px-3 py-1.5 border border-gray-300 text-sm font-medium rounded text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
                            >
                              {downloading === doc.id ? (
                                <>
                                  <Loader className="h-4 w-4 mr-1 animate-spin" />
                                  Downloading...
                                </>
                              ) : (
                                <>
                                  <Download className="h-4 w-4 mr-1" />
                                  {doc.redacted ? 'Download Preview' : 'Download'}
                                </>
                              )}
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          )
        }))}

        {/* Footer Notice */}
        <div className="mt-12 p-6 bg-gray-100 rounded-lg">
          <h3 className="text-sm font-medium text-gray-900 mb-2">Important Notice</h3>
          <ul className="text-sm text-gray-600 space-y-1">
            <li>• All document access is logged and monitored</li>
            <li>• Documents may contain dynamic watermarks with your information</li>
            <li>• Access is time-limited and subject to the terms of your NDA</li>
            <li>• Unauthorized sharing or distribution is prohibited</li>
          </ul>
        </div>
      </div>

      {/* Bulk Edit Modal */}
      {showBulkEditModal && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-full max-w-2xl shadow-lg rounded-md bg-white">
            <div className="flex items-center justify-between mb-4">
              <h3 className="text-lg font-semibold text-gray-900">
                Bulk Edit Documents ({selectedDocs.size} selected)
              </h3>
              <button
                onClick={() => setShowBulkEditModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X className="h-6 w-6" />
              </button>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 mb-4">
                Select the attributes you want to update. Only selected attributes will be changed.
              </p>

              {/* Visibility */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={bulkEditForm.visibility !== undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBulkEditForm({ ...bulkEditForm, visibility: 'public' })
                      } else {
                        const { visibility, ...rest } = bulkEditForm
                        setBulkEditForm(rest)
                      }
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Update Visibility</span>
                </label>
                {bulkEditForm.visibility !== undefined && (
                  <select
                    value={bulkEditForm.visibility}
                    onChange={(e) => setBulkEditForm({ ...bulkEditForm, visibility: e.target.value as 'public' | 'gated' | 'admin' })}
                    className="mt-2 ml-6 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="public">Public</option>
                    <option value="gated">Gated</option>
                    <option value="admin">Admin Only</option>
                  </select>
                )}
              </div>

              {/* Gated */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={bulkEditForm.gated !== undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBulkEditForm({ ...bulkEditForm, gated: true })
                      } else {
                        const { gated, ...rest } = bulkEditForm
                        setBulkEditForm(rest)
                      }
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Update Gated Access</span>
                </label>
                {bulkEditForm.gated !== undefined && (
                  <select
                    value={bulkEditForm.gated ? 'true' : 'false'}
                    onChange={(e) => setBulkEditForm({ ...bulkEditForm, gated: e.target.value === 'true' })}
                    className="mt-2 ml-6 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="true">Gated (require login)</option>
                    <option value="false">Not Gated (public)</option>
                  </select>
                )}
              </div>

              {/* NDA Required */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={bulkEditForm.ndaRequired !== undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBulkEditForm({ ...bulkEditForm, ndaRequired: true })
                      } else {
                        const { ndaRequired, ...rest } = bulkEditForm
                        setBulkEditForm(rest)
                      }
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Update NDA Requirement</span>
                </label>
                {bulkEditForm.ndaRequired !== undefined && (
                  <select
                    value={bulkEditForm.ndaRequired ? 'true' : 'false'}
                    onChange={(e) => setBulkEditForm({ ...bulkEditForm, ndaRequired: e.target.value === 'true' })}
                    className="mt-2 ml-6 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="true">NDA Required</option>
                    <option value="false">No NDA Required</option>
                  </select>
                )}
              </div>

              {/* Watermarked */}
              <div>
                <label className="flex items-center space-x-2">
                  <input
                    type="checkbox"
                    checked={bulkEditForm.watermarked !== undefined}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setBulkEditForm({ ...bulkEditForm, watermarked: true })
                      } else {
                        const { watermarked, ...rest } = bulkEditForm
                        setBulkEditForm(rest)
                      }
                    }}
                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                  />
                  <span className="text-sm font-medium text-gray-700">Update Watermark</span>
                </label>
                {bulkEditForm.watermarked !== undefined && (
                  <select
                    value={bulkEditForm.watermarked ? 'true' : 'false'}
                    onChange={(e) => setBulkEditForm({ ...bulkEditForm, watermarked: e.target.value === 'true' })}
                    className="mt-2 ml-6 block w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-2 focus:ring-primary-500 focus:border-primary-500"
                  >
                    <option value="true">Apply Watermark</option>
                    <option value="false">No Watermark</option>
                  </select>
                )}
              </div>
            </div>

            {/* Modal Actions */}
            <div className="mt-6 flex items-center justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkEditModal(false)
                  setBulkEditForm({})
                }}
                disabled={bulkEditUpdating}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkEditSubmit}
                disabled={bulkEditUpdating}
                className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-primary-600 hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {bulkEditUpdating ? (
                  <>
                    <Loader className="h-4 w-4 mr-2 animate-spin" />
                    Updating...
                  </>
                ) : (
                  <>
                    <Save className="h-4 w-4 mr-2" />
                    Update Selected
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default EvidenceLibrary